import { useEffect, useState } from "react";
import { Sparkles, Save, RefreshCw, ToggleRight } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const PLAN_OPTIONS = [
  { v: "pro_plus", label: "Pro Plus (8 GB, AI Drafts)" },
  { v: "pro_standard", label: "Pro (4 GB)" },
];

export default function AdminFreeTrial() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [claimed, setClaimed] = useState(0);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/free-trial");
      setCfg({
        enabled: !!r.data.enabled,
        plan: r.data.plan || "pro_plus",
        days: r.data.days ?? 7,
        headline: r.data.headline || "",
        subhead: r.data.subhead || "",
      });
      setClaimed(r.data.claimed_count || 0);
    } catch {
      toast.error("Could not load free trial settings");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const r = await api.put("/admin/free-trial", patch);
      setCfg({
        enabled: !!r.data.enabled,
        plan: r.data.plan,
        days: r.data.days,
        headline: r.data.headline,
        subhead: r.data.subhead,
      });
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  if (!cfg) {
    return <div className="p-12 text-center font-bold">Loading…</div>;
  }

  return (
    <div className="p-6 sm:p-8 lg:p-12 max-w-3xl">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Admin · Promotions</p>
          <h1 className="font-display text-4xl font-black mt-1 flex items-center gap-3">
            <Sparkles className="w-8 h-8" strokeWidth={2.5}/> Free Trial Popup
          </h1>
          <p className="text-base font-medium text-slate-600 mt-2">
            Toggle a one-time, no-card-needed trial offer that shows on first login for free users.
          </p>
        </div>
        <button onClick={load} disabled={busy} className="nb-btn-secondary inline-flex items-center gap-2" data-testid="admin-ft-refresh">
          <RefreshCw className="w-4 h-4" strokeWidth={2.5}/> Refresh
        </button>
      </header>

      <div className="nb-card p-6 mb-6 bg-yellow-100">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest">Status</div>
            <div className="font-display text-2xl font-black mt-1">{cfg.enabled ? "LIVE — showing to free users" : "Off"}</div>
            <div className="text-xs text-slate-600 mt-1">{claimed} user{claimed === 1 ? "" : "s"} have claimed so far.</div>
          </div>
          <button
            data-testid="admin-ft-toggle"
            onClick={() => save({ enabled: !cfg.enabled })}
            disabled={busy}
            className={`nb-btn-primary inline-flex items-center gap-2 ${cfg.enabled ? "!bg-emerald-300" : "!bg-white"}`}
          >
            <ToggleRight className="w-5 h-5" strokeWidth={2.5}/>
            {cfg.enabled ? "Turn off" : "Turn on"}
          </button>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          save({
            plan: cfg.plan,
            days: Number(cfg.days),
            headline: cfg.headline,
            subhead: cfg.subhead,
          });
        }}
        className="nb-card p-6 space-y-5"
      >
        <div>
          <label className="block text-xs uppercase tracking-widest font-bold mb-2">Plan offered</label>
          <select
            data-testid="admin-ft-plan"
            value={cfg.plan}
            onChange={(e) => setCfg({ ...cfg, plan: e.target.value })}
            className="nb-input"
          >
            {PLAN_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-widest font-bold mb-2">Length (days, 1–60)</label>
          <input
            data-testid="admin-ft-days"
            type="number" min={1} max={60}
            value={cfg.days}
            onChange={(e) => setCfg({ ...cfg, days: e.target.value })}
            className="nb-input"
          />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-widest font-bold mb-2">Headline</label>
          <input
            data-testid="admin-ft-headline"
            value={cfg.headline}
            onChange={(e) => setCfg({ ...cfg, headline: e.target.value })}
            className="nb-input"
            maxLength={120}
            placeholder="Try Pro Plus free for 7 days"
          />
        </div>

        <div>
          <label className="block text-xs uppercase tracking-widest font-bold mb-2">Subheading</label>
          <textarea
            data-testid="admin-ft-subhead"
            value={cfg.subhead}
            onChange={(e) => setCfg({ ...cfg, subhead: e.target.value })}
            rows={3}
            className="nb-input"
            maxLength={240}
            placeholder="Unlimited AI quizzes, AI drafts, 8GB storage. No card required."
          />
        </div>

        <button type="submit" disabled={busy} data-testid="admin-ft-save" className="nb-btn-primary inline-flex items-center gap-2">
          <Save className="w-4 h-4" strokeWidth={2.5}/> Save changes
        </button>
      </form>

      <p className="text-xs text-slate-500 mt-6">
        Tip: leave this off until you're ready to promote. Each user can only claim once.
      </p>
    </div>
  );
}
